import React, { useEffect } from "react";

// Sections are rendered after the SPA mounts, so the browser's native hash jump misses them.
const useHashScroll = () => {
  useEffect(() => {
    const go = () => {
      const id = decodeURIComponent(window.location.hash.slice(1));
      const el = id && document.getElementById(id);
      if (el) el.scrollIntoView({ block: "start" });
    };
    const t = setTimeout(go, 150);
    window.addEventListener("hashchange", go);
    return () => { clearTimeout(t); window.removeEventListener("hashchange", go); };
  }, []);
};
import { useSeo } from "@/lib/useSeo";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Phone,
  PaperPlaneTilt,
  ShieldCheck,
  UsersThree,
  Headset,
  Tag,
  Wrench,
  Truck,
  Sparkle,
  Broom,
  Couch,
  House,
  Stack,
  MapPin,
  Buildings,
} from "@phosphor-icons/react";
import QuoteRequestForm from "@/components/QuoteRequestForm";
import { BeforeAfterSlideshow, useFreshStartPairs } from "@/components/BeforeAfterSlideshow";
import { MobileActionBar } from "@/components/MobileActionBar";
import { HowItWorksDiagram } from "@/components/HowItWorksDiagram";
import { SiteHeader, PHONE_DISPLAY, PHONE_HREF } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useHomepageConfig, quoteHrefForService, jumpToQuote } from "@/config/homepage";

// ── Brand palette ──────────────────────────────────────────────────────────
const NAVY = "#182848";
const ORANGE = "#EE7623";
const ORANGE_DARK = "#D4651C";
const SLATE = "#4A5A72";
const CREAM = "#FDF3EA";
const WHITE = "#FFFFFF";
const LINE = "rgba(24,40,72,0.14)";

const NAV_ANCHORS = [
  { href: "#services", label: "What we do" },
  { href: "#how-it-works", label: "How it works" },
  { href: "#before-after", label: "Before & After", needsPairs: true },
  { href: "#quote", label: "Get a Quote", primary: true },
];

const CARD_ICONS = {
  "junk-removal": Truck,
  "deep-cleaning": Sparkle,
  "move-out-cleaning": Broom,
  "carpet-cleaning": Couch,
  "whole-house-cleanout": House,
  declutter: Stack,
};

const WHY_US = [
  { Icon: UsersThree, title: "Vetted, Managed Crews", body: "Every crew in our network is screened and works under our operations team. Not whoever answered the phone." },
  { Icon: Headset, title: "One Point of Contact", body: "One call, one quote, one invoice — even when a job needs four different trades. You never chase anybody." },
  { Icon: Tag, title: "Real Prices, Published", body: "Our rates are on this site. No booking a visit just to find out what something costs." },
  { Icon: Wrench, title: "We Can Fix What's Underneath", body: "Clearing a space usually reveals the next job — carpet that should go, walls that need paint, a floor that needs repair. Most cleaning and hauling companies hand you a list of other people to call. We just handle it." },
];

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } } };

const Eyebrow = ({ children, light }) => (
  <div className="font-mono-label text-[10px] tracking-[0.28em]" style={{ color: light ? "rgba(255,255,255,0.7)" : ORANGE }}>{children}</div>
);
const H2 = ({ children, testid, light }) => (
  <h2 className="font-display mt-4 font-black leading-[1.02] tracking-tighter" style={{ color: light ? WHITE : NAVY, fontSize: "clamp(30px, 4vw, 52px)" }} data-testid={testid}>{children}</h2>
);

// ── Page ───────────────────────────────────────────────────────────────────
export default function CustomersPage() {
  const pairs = useFreshStartPairs();
  const { services, core_services: coreServices } = useHomepageConfig();
  const strip = services.filter((s) => s.strip);
  useHashScroll();

  const hasPairs = !!(pairs && pairs.length);
  const navAnchors = NAV_ANCHORS.filter((a) => !a.needsPairs || hasPairs);
  useSeo({
    title: "Junk Removal, Deep Cleaning & Cleanouts in Baltimore | HCOB Property Services",
    description: "Junk removal, deep cleaning, carpet care and whole-home cleanouts across Baltimore and all of Maryland — vetted local crews, one point of contact, real published prices.",
    path: "/",
  });

  return (
    <div data-testid="customers-page" className="min-h-screen antialiased" style={{ backgroundColor: WHITE, color: NAVY }}>
      <SiteHeader anchors={navAnchors} />

      {/* ── 1. Hero ──────────────────────────────────────────────────── */}
      <section id="top" data-testid="customers-hero" style={{ backgroundColor: NAVY, color: WHITE }}>
        <motion.div initial="hidden" animate="show" variants={stagger} className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 py-14 md:px-10 md:py-16 lg:grid-cols-12 lg:items-end lg:py-20">
          <div className="lg:col-span-8">
            <motion.div variants={fadeUp} className="font-mono-label flex items-center gap-2 text-[10px] tracking-[0.26em] text-white/70">
              <span className="inline-block h-1.5 w-6" style={{ backgroundColor: ORANGE }} />
              Baltimore, MD · Serving all of Maryland
            </motion.div>
            <motion.h1 data-testid="customers-hero-title" variants={fadeUp} className="font-display mt-7 text-4xl font-black leading-[0.96] tracking-tighter sm:text-5xl lg:text-[64px]">
              Cleaned Out, Cleaned Up,
              <br className="hidden sm:block" />{" "}
              <span style={{ color: ORANGE }}>Done Right.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-6 max-w-[620px] text-[15px] leading-[1.65] text-white/80 md:text-base" data-testid="hero-subheadline">
              Junk removal, deep cleaning, carpet care and whole-home cleanouts across Baltimore and all of Maryland — handled by vetted local crews under one point of contact. Real prices, no walkthrough required.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                data-testid="hero-quote-cta"
                href="#quote"
                className="group inline-flex h-14 items-center justify-center gap-2 rounded-sm px-7 text-[14px] font-black text-white transition-colors"
                style={{ backgroundColor: ORANGE }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = ORANGE_DARK)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = ORANGE)}
              >
                <PaperPlaneTilt size={16} weight="fill" /> Get My Free Quote
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </a>
              <a data-testid="hero-call-cta" href={PHONE_HREF} className="inline-flex h-14 items-center justify-center gap-2 rounded-sm border border-white/30 px-6 text-[13px] font-bold text-white hover:bg-white/10">
                <Phone size={16} weight="fill" /> Call or Text {PHONE_DISPLAY}
              </a>
            </motion.div>
            <motion.p variants={fadeUp} className="mt-5 text-[12px] text-white/60" data-testid="hero-trust-strip">
              Vetted local crews &nbsp;•&nbsp; Prices published, not negotiated &nbsp;•&nbsp; Based in Gwynn Oak, serving all of Maryland
            </motion.p>
            <motion.p variants={fadeUp} className="mt-4 text-[14px] text-white/85">
              Need it today? <a href="/same-day-cleaning" data-testid="need-it-today" className="font-bold underline underline-offset-4" style={{ color: ORANGE }}>Same-day cleaning in Baltimore — request by noon.</a>
            </motion.p>
          </div>

          {/* Stat row — horizontal on mobile, stacked panel on desktop */}
          <motion.div variants={fadeUp} className="grid grid-cols-3 gap-6 border-t border-white/15 pt-8 lg:col-span-4 lg:grid-cols-1 lg:gap-7 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0" data-testid="hero-stat-row">
            {[
              { k: "13+", l: "Trade services" },
              { k: "1", l: "Point of contact" },
              { k: "MD", l: "Statewide coverage" },
            ].map((s) => (
              <div key={s.l} data-testid={`hero-stat-${s.l.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="font-display text-3xl font-black leading-none tracking-tight lg:text-4xl" style={{ color: ORANGE }}>{s.k}</div>
                <div className="font-mono-label mt-2 text-[10px] tracking-[0.22em] text-white/80">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* ── 2. Six core services ─────────────────────────────────────── */}
      <section id="services" data-testid="customers-core-services" className="border-b" style={{ backgroundColor: WHITE, borderColor: LINE }}>
        <div className="mx-auto max-w-[1400px] px-5 py-12 md:px-10 md:py-16">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <Eyebrow>01 · What we do</Eyebrow>
              <h2 className="font-display mt-3 text-2xl font-black leading-[1.05] tracking-tight sm:text-3xl" style={{ color: NAVY }} data-testid="core-services-heading">Six things we do every week.</h2>
            </div>
            <p className="max-w-[420px] text-[14px] leading-relaxed" style={{ color: SLATE }}>Pick a service to see real prices, or ask for a quote on anything else below.</p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="core-services-grid">
            {coreServices.map((c) => {
              const Icon = CARD_ICONS[c.slug] || Sparkle;
              return (
                <article
                  key={c.slug}
                  data-testid={`core-card-${c.slug}`}
                  className="group relative flex flex-col justify-between rounded-sm border p-7 transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-1 hover:shadow-[0_18px_40px_-20px_rgba(24,40,72,0.45)] focus-within:ring-2 focus-within:ring-[#EE7623] focus-within:ring-offset-2"
                  style={{ borderColor: LINE, backgroundColor: CREAM }}
                >
                  {/* Stretched link: whole card goes to the editable destination */}
                  <a href={c.href} data-testid={`core-card-link-${c.slug}`} className="absolute inset-0 z-[1] rounded-sm outline-none" aria-label={`${c.title} — see details`} />
                  <div>
                    <div className="flex items-start justify-between gap-3">
                      <div className="grid h-12 w-12 place-items-center rounded-sm" style={{ backgroundColor: NAVY, color: WHITE }}>
                        <Icon size={24} weight="duotone" />
                      </div>
                      {c.price ? (
                        <span className="rounded-sm px-2.5 py-1 font-mono-label text-[10px] font-bold tracking-[0.14em] text-white" style={{ backgroundColor: ORANGE }} data-testid={`core-card-price-${c.slug}`}>
                          {c.price}
                        </span>
                      ) : null}
                    </div>
                    <h3 className="font-display mt-6 text-[22px] font-black leading-tight tracking-tight" style={{ color: NAVY }}>{c.title}</h3>
                    <p className="mt-3 text-[14.5px] leading-[1.6]" style={{ color: SLATE }}>{c.desc}</p>
                  </div>
                  <div className="mt-7 flex items-center justify-between gap-3 border-t pt-4" style={{ borderColor: LINE }}>
                    <span className="inline-flex items-center gap-1 text-[13px] font-bold" style={{ color: NAVY }}>
                      See details <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-1" />
                    </span>
                    <a
                      href={quoteHrefForService(c.slug)}
                      onClick={(e) => jumpToQuote(e, c.slug)}
                      data-testid={`core-card-quote-${c.slug}`}
                      className="relative z-[2] inline-flex min-h-[44px] items-center rounded-sm px-3 text-[12px] font-black underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#EE7623]"
                      style={{ color: ORANGE }}
                    >
                      Quick quote →
                    </a>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 3. How it works diagram ──────────────────────────────────── */}
      <section id="how-it-works" data-testid="how-it-works-section" className="border-b" style={{ backgroundColor: CREAM, color: NAVY, borderColor: LINE }}>
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 py-14 md:px-10 md:py-24 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-4">
            <Eyebrow>02 · How it works</Eyebrow>
            <H2 testid="how-it-works-heading">You don't hire four contractors. You hire one outcome.</H2>
            <p className="mt-5 text-[15px] leading-[1.65] md:text-base" style={{ color: SLATE }}>
              One call. We scope it, price it, send the right crews in the right order, and hand you one finished result.
            </p>
            <a href="#quote" data-testid="how-it-works-quote-link" className="mt-6 inline-flex min-h-[44px] items-center gap-1 text-[13px] font-bold" style={{ color: ORANGE }}>
              Start with a quote <ArrowRight size={14} weight="bold" />
            </a>
          </div>
          <div className="lg:col-span-8">
            <HowItWorksDiagram />
          </div>
        </div>
      </section>

      {/* ── 4. Why us ────────────────────────────────────────────────── */}
      <section id="why-us" data-testid="customers-why-us" className="border-b" style={{ backgroundColor: WHITE, borderColor: LINE }}>
        <div className="mx-auto max-w-[1400px] px-5 py-14 md:px-10 md:py-24">
          <Eyebrow>03 · Why us</Eyebrow>
          <H2 testid="why-us-heading">What Makes Us Different</H2>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger} className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {WHY_US.map(({ Icon, title, body }) => (
              <motion.div key={title} variants={fadeUp} className="rounded-sm border p-6" style={{ borderColor: LINE }} data-testid={`why-us-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <Icon size={26} weight="duotone" style={{ color: ORANGE }} />
                <h3 className="font-display mt-4 text-[17px] font-black uppercase leading-tight tracking-[0.04em]" style={{ color: NAVY }}>{title}</h3>
                <p className="mt-3 text-[14px] leading-[1.65]" style={{ color: SLATE }}>{body}</p>
              </motion.div>
            ))}
          </motion.div>
          <p className="font-mono-label mt-10 text-[11px] tracking-[0.3em]" style={{ color: ORANGE }} data-testid="why-us-tagline">
            By the Community. For the Community.
          </p>
        </div>
      </section>

      {/* ── 5. Full capability strip ─────────────────────────────────── */}
      <section id="all-services" data-testid="customers-capability-strip" className="border-b" style={{ backgroundColor: CREAM, borderColor: LINE }}>
        <div className="mx-auto max-w-[1400px] px-5 py-12 md:px-10 md:py-20">
          <Eyebrow>04 · And 13+ more trades</Eyebrow>
          <H2 testid="capability-heading">And Everything Else a Property Needs</H2>
          <p className="mt-4 max-w-[600px] text-[15px] leading-relaxed" style={{ color: SLATE }}>
            The six above are what we're known for. They're not the whole list.
          </p>
          <ul className="mt-8 flex flex-wrap gap-2" data-testid="capability-chips" aria-label="All services">
            {strip.map((s) => (
              <li key={s.slug}>
                <a
                  href={s.slug === "estate-cleanouts" ? "/estate-cleanout" : quoteHrefForService(s.slug)}
                  onClick={s.slug === "estate-cleanouts" ? undefined : (e) => jumpToQuote(e, s.slug)}
                  data-testid={`capability-chip-${s.slug}`}
                  className="inline-flex min-h-[48px] items-center rounded-sm border bg-white px-4 text-[13px] font-semibold transition-colors hover:border-[#182848] hover:bg-[#182848] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#EE7623]"
                  style={{ borderColor: LINE, color: NAVY }}
                >
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-[14px]" style={{ color: SLATE }}>
            Don't see what you need?{" "}
            <a href={quoteHrefForService("other")} onClick={(e) => jumpToQuote(e, "other")} className="font-bold underline underline-offset-2" style={{ color: NAVY }} data-testid="capability-ask-link">Ask</a>{" "}
            — we probably handle it.
          </p>
        </div>
      </section>

      {/* ── 6. Proof (before/after) ──────────────────────────────────── */}
      {hasPairs && (
        <section id="before-after" data-testid="customers-before-after" className="relative overflow-hidden" style={{ backgroundColor: NAVY, color: WHITE }}>
          <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(#FFFFFF 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
          <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 py-16 md:grid-cols-12 md:px-10 md:py-24">
            <div className="md:col-span-5">
              <Eyebrow light>05 · Before &amp; after</Eyebrow>
              <H2 light>Real homes. <span style={{ color: ORANGE }}>Real resets.</span></H2>
              <p className="mt-6 text-[15px] leading-[1.75] text-white/80 md:text-base">
                Deep clean, junk and clutter removal, and carpet treatment — one crew, one visit. Every photo here is a Maryland home, shared with the owner's permission.
              </p>
              <a href="/fresh-start" data-testid="before-after-cta" className="mt-8 inline-flex h-12 items-center gap-2 rounded-sm px-6 text-[13px] font-black text-white transition-opacity hover:opacity-90" style={{ backgroundColor: ORANGE }}>
                See the Fresh Start program <ArrowRight size={15} />
              </a>
            </div>
            <div className="md:col-span-7">
              <BeforeAfterSlideshow pairs={pairs} accent={ORANGE} />
            </div>
          </div>
        </section>
      )}

      {/* ── 7. Service area teaser ───────────────────────────────────── */}
      <section id="service-area" data-testid="customers-service-area" className="border-b" style={{ backgroundColor: WHITE, borderColor: LINE }}>
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-8 px-5 py-14 md:grid-cols-12 md:items-center md:px-10 md:py-20">
          <div className="md:col-span-8">
            <Eyebrow>06 · Where we work</Eyebrow>
            <H2 testid="service-area-teaser-heading">Based in Gwynn Oak. Working all of Maryland.</H2>
            <p className="mt-4 max-w-[640px] text-[15px] leading-relaxed md:text-base" style={{ color: SLATE }}>
              Our crews are in Pikesville, Catonsville, Randallstown, Windsor Mill, Ellicott City and Baltimore City most days of the week — and we take jobs across the whole state. Check your ZIP in seconds.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <a href="/service-area" data-testid="service-area-teaser-cta" className="inline-flex h-14 items-center gap-2 rounded-sm border-2 px-6 text-[14px] font-black transition-colors hover:bg-[#182848] hover:text-white" style={{ borderColor: NAVY, color: NAVY }}>
              <MapPin size={16} weight="fill" /> Check my ZIP <ArrowRight size={16} weight="bold" />
            </a>
          </div>
        </div>
      </section>

      {/* ── 8. Property manager callout ──────────────────────────────── */}
      <section id="property-managers" data-testid="customers-pm-callout" className="border-b" style={{ backgroundColor: CREAM, borderColor: LINE }}>
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-8 px-5 py-14 md:grid-cols-12 md:items-center md:px-10 md:py-20">
          <div className="md:col-span-8">
            <Eyebrow>07 · For property managers</Eyebrow>
            <H2 testid="pm-callout-heading">Manage Properties?</H2>
            <p className="mt-4 max-w-[640px] text-[15px] leading-relaxed md:text-base" style={{ color: SLATE }}>
              Turnovers, eviction cleanouts and multi-unit programs, run as one managed job instead of three vendors.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <a href="/property-managers" data-testid="pm-callout-link" className="inline-flex min-h-[48px] items-center gap-2 text-[15px] font-black underline-offset-4 hover:underline" style={{ color: NAVY }}>
              <Buildings size={18} weight="duotone" style={{ color: ORANGE }} /> See how we work with property managers <ArrowRight size={16} weight="bold" />
            </a>
          </div>
        </div>
      </section>

      {/* ── 9. Quote form ────────────────────────────────────────────── */}
      <section id="quote" data-testid="customers-quote" style={{ backgroundColor: WHITE }}>
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 py-14 md:grid-cols-12 md:px-10 md:py-24">
          <div className="md:col-span-5">
            <Eyebrow>08 · Get a quote</Eyebrow>
            <H2>Send us the basics. <span style={{ color: ORANGE }}>We'll text you back.</span></H2>
            <p className="mt-6 text-[15px] leading-relaxed" style={{ color: SLATE }}>
              Tell us what you need and we'll line up the right crew. No accounts, no logins, no spam — just a quick reply from a real person.
            </p>
            <ul className="mt-8 space-y-4">
              {["Free estimates · no obligation", "Prices published on this site", "One point of contact for every service"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[14px]" style={{ color: NAVY }}>
                  <span className="mt-[9px] inline-block h-[6px] w-8 shrink-0" style={{ backgroundColor: ORANGE }} />
                  {t}
                </li>
              ))}
            </ul>
            <div className="mt-10 rounded-sm border p-5" style={{ borderColor: LINE, backgroundColor: CREAM }}>
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} weight="fill" style={{ color: NAVY }} />
                <div className="font-mono-label text-[10px] tracking-[0.24em]" style={{ color: SLATE }}>We never share your info</div>
              </div>
              <div className="mt-2 text-[13px]" style={{ color: SLATE }}>
                Or dial <a href={PHONE_HREF} className="font-bold underline underline-offset-2" style={{ color: NAVY }}>{PHONE_DISPLAY}</a> and talk to a coordinator directly.
              </div>
            </div>
          </div>
          <div className="order-first md:order-none md:col-span-7">
            <div className="rounded-sm border bg-white p-4 md:p-6" style={{ borderColor: LINE }}>
              <QuoteRequestForm />
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
      <MobileActionBar primaryLabel="Get My Free Quote" primaryHref="#quote" phoneHref={PHONE_HREF} textBody="Hi HCOB Property Services — I'd like a quote for " watch={["#top", "#quote"]} accent={ORANGE} testid="customers-mobile-bar" />
    </div>
  );
}
